import { useContext, useEffect, useRef, useState } from 'react'
import Select from 'react-select'
import CheckBox from '../../common/CheckBox'
import DtcTable from './DtcTable'
import { createSearchParams, useNavigate, useParams } from 'react-router-dom'
import { NavContext } from '../../../context/global_context'
import { getRequest, postRequest } from '../../../services/base'
import EditMappings from './EditMappings'
import { RESET_SELECTED_DTC, TatContext } from './context/context'
import Button from '../../common/Button'
import {
  getSessionStorageData,
  setSessionStorageData,
} from '../../../utils/SessionStorage'

const DtcsListing = () => {
  const interval = useRef(null)

  let filterData = getSessionStorageData('dtcs_list')
  let { vehicle } = useParams()
  const navigate = useNavigate()
  const [ecusData, setEcusData] = useState([])
  const [dtcsStatus, setDtcsStatus] = useState({
    enable_mapping: true,
    enable_generate_wire: false,
  })
  const [value, dispatch] = useContext(NavContext)
  const [tatData, tatDataDispatch] = useContext(TatContext)

  const [searchValue, setSearchValue] = useState(
    filterData?.searchValue ? filterData?.searchValue : null
  )

  const [ecu, setEcu] = useState(
    filterData?.ecu?.value ? filterData?.ecu : null
  )
  const [dtcData, setDtcData] = useState([])
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    current_page: 1,
  })
  const [hasNoConnectorMapping, setHasNoConnectorMapping] = useState(
    filterData?.hasNoConnectorMapping
      ? filterData?.hasNoConnectorMapping
      : 'yes'
  )
  const [active, setActive] = useState(
    filterData?.active ? filterData?.active : 'yes'
  )
  const [destinationOnly, setDestinationOnly] = useState(
    filterData?.destinationOnly ? filterData?.destinationOnly : 'no'
  )
  const currentUrl = useRef(
    `/api/dtcs/?search=${
      searchValue ? searchValue : ''
    }&vehicle=${vehicle}&has_no_con_mapping=${hasNoConnectorMapping}&destination_only=${destinationOnly}&active=${active}&ecu=${
      ecu ? ecu : ''
    }&limit=7`
  )

  useEffect(() => {
    setDefaultValues()
    getEcuData('')
    callDtcsStatusInterval()
    getDtcsStatus()
    dispatch({
      type: 'header',
      data: {
        header: 'Admin Tools',
        sub_header: `Vehicle List / ${vehicle} / DTCs / Tracer Authoring Mappings`,
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: getSessionStorageData('vehicle')?.search
          ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
          : `/vehicles`,
      },
    })

    return () => {
      if (interval.current) {
        clearInterval(interval.current)
      }
    }
  }, [])

  const callDtcsStatusInterval = () => {
    interval.current = setInterval(() => {
      getDtcsStatus()
    }, 10000)
  }

  const setDefaultValues = () => {
    currentUrl.current = `/api/dtcs/?search=${
      searchValue ? searchValue : ''
    }&vehicle=${vehicle}&has_no_con_mapping=${hasNoConnectorMapping}&destination_only=${destinationOnly}&active=${active}&ecu=${
      ecu?.value ? ecu?.value : ''
    }&limit=7`
    getDtcsData()
  }

  const delayedFunction = (
    search,
    has_no_con_mapping,
    destination_only,
    active,
    ecu
  ) => {
    setTimeout(function () {
      setSessionStorageData('dtcs_list', {
        searchValue: search,
        ecu,
        hasNoConnectorMapping: has_no_con_mapping,
        active,
        destinationOnly: destination_only,
      })

      currentUrl.current = `/api/dtcs/?search=${
        search ? search : ''
      }&vehicle=${vehicle}&has_no_con_mapping=${has_no_con_mapping}&destination_only=${destination_only}&active=${active}&ecu=${
        ecu?.value ? ecu?.value : ''
      }&limit=7`
      getDtcsData()
    }, 500)
  }

  const getDtcsData = async () => {
    dispatch({
      type: 'loader',
      data: {
        caption: 'Fetching dtcs Data',
        showLoader: true,
      },
      notification: {},
    })

    let response = await getRequest(currentUrl.current)
    setDtcData(response?.results)
    setPagination(response?.pagination)
    dispatch({
      type: 'loader',
      data: {},
      notification: {},
    })
  }

  const getEcuData = async (search) => {
    let response = await getRequest(
      `/api/ecu/ecu/?vehicle=${vehicle}&search=${search}`
    )
    if (response?.data?.length > 0) {
      setEcusData(response.data)
    }
  }

  const getDtcsStatus = async () => {
    let response = await getRequest(`/api/dtcs/dtcs_status/?vehicle=${vehicle}`)
    setDtcsStatus(response)
  }

  const getPaginatedData = async (url) => {
    let response = await getRequest(url)
    if (response?.results?.length > 0) {
      setDtcData(response.results)
      setPagination(response.pagination)
    }
  }

  const resetFilterData = () => {
    setEcu(null)
    setSearchValue(null)
    setDestinationOnly('no')
    setActive('yes')
    setHasNoConnectorMapping('yes')
    delayedFunction('', 'yes', 'no', 'yes', '')
  }

  const generateWires = async () => {
    await postRequest(`/api/wire/generate_wires/`, {
      vehicle: vehicle,
      is_inspection_points_wire: false,
      is_power_distribution_wire: false,
    })
    getDtcsStatus()
  }

  const deleteSelectedDtc = async () => {
    if (window.confirm(`Are you sure, really want to delete dtc`) == true) {
      let payload = { vehicle: vehicle, selected_dtc: tatData.selected_dtc }
      let res = await postRequest(
        `/api/dtcs/delete_dtc/?vehicle=${vehicle}`,
        payload
      )
      if (res.status) {
        getDtcsData()
        dispatch({
          type: 'notification',
          data: {
            notification_type: 'success',
            msg: 'Dtc Deleted Successfully!',
          },
        })
        tatDataDispatch({ type: RESET_SELECTED_DTC, data: [] })
      } else {
        dispatch({
          type: 'notification',
          data: {
            notification_type: 'error',
            msg: res.msg,
          },
        })
      }
    }
  }

  const createNewDtc = () => {
    navigate(`/vehicles/${vehicle}/dtcs/listing/new`)
  }

  return (
    <div className="flex flex-col py-3 space-y-6">
      <div className="flex justify-between items-center text-white bg-tracer-dark-blue rounded py-3 px-6">
        <div className="flex space-x-4 items-center">
          <span>Filter</span>
          <input
            type="serach"
            name="dtcs_search"
            placeholder="Enter serach query"
            className="w-56 h-10 px-5 rounded focus:outline-none text-black"
            onChange={(e) => {
              setSearchValue(e.target.value)
              delayedFunction(
                e.target.value,
                hasNoConnectorMapping,
                destinationOnly,
                active,
                ecu
              )
            }}
            defaultValue={searchValue}
          />
          <Select
            classNamePrefix="dtcs-filter"
            isMulti={false}
            value={ecusData.filter((option) => option.label === ecu?.label)}
            options={ecusData}
            placeholder="Select ECU"
            className="w-56 cursor-pointer"
            onChange={(value) => {
              setEcu(value)
              delayedFunction(
                searchValue,
                hasNoConnectorMapping,
                destinationOnly,
                active,
                value
              )
            }}
            defaultValue={ecu?.label}
            isClearable={true}
          />

          <CheckBox
            checked={hasNoConnectorMapping === 'yes' ? true : false}
            onChange={(e) => {
              if (!e.target.checked) {
                setHasNoConnectorMapping('no')
              } else {
                setHasNoConnectorMapping('yes')
              }
              delayedFunction(
                searchValue,
                !e.target.checked ? 'no' : 'yes',
                destinationOnly,
                active,
                ecu
              )
            }}
          />
          <span>Has no connector mappings</span>

          <CheckBox
            checked={active === 'yes' ? true : false}
            onChange={(e) => {
              if (!e.target.checked) {
                setActive('no')
              } else {
                setActive('yes')
              }
              delayedFunction(
                searchValue,
                hasNoConnectorMapping,
                destinationOnly,
                e.target.checked ? 'yes' : 'no',
                ecu
              )
            }}
          />
          <span>Active</span>

          <CheckBox
            checked={destinationOnly === 'yes' ? true : false}
            onChange={(e) => {
              if (!e.target.checked) {
                setDestinationOnly('no')
              } else {
                setDestinationOnly('yes')
              }
              delayedFunction(
                searchValue,
                hasNoConnectorMapping,
                !e.target.checked ? 'no' : 'yes',
                active,
                ecu
              )
            }}
          />
          <span>Destination Only</span>
          <a
            className="underline cursor-pointer"
            onClick={() => {
              resetFilterData()
            }}
          >
            Clear Filters
          </a>
        </div>
      </div>
      <DtcTable
        dtcs={dtcData}
        pagination={pagination}
        getPaginatedData={getPaginatedData}
        vehicleSid={vehicle}
      />

      <div className="flex  space-x-4 p-4 bg-tracer-dark-blue">
        <div className={`${!dtcsStatus.enable_mapping && 'disabled'}`}>
          <EditMappings
            selectedDtcs={tatData?.selected_dtc}
            sid={vehicle}
            filters={{
              searchValue,
              ecu,
              hasNoConnectorMapping,
              active,
              destinationOnly,
            }}
          />
        </div>

        <Button
          type="submit"
          className={`cursor-pointer btn ${!dtcsStatus.enable_generate_wire && 'disabled'}`}
          onClick={(e) => {
            generateWires()
          }}
          text={'Regenerate Wiring Circuit Graph'}
        />
        <Button
          type="submit"
          className={`cursor-pointer btn ${tatData.selected_dtc < 1 && 'disabled'}`}
          onClick={(e) => {
            deleteSelectedDtc()
          }}
          text={'Delete Selected DTC'}
        />
        <Button
          type="submit"
          className={`cursor-pointer btn`}
          onClick={(e) => {
            createNewDtc()
          }}
          text={'Create New DTC'}
        />
      </div>
    </div>
  )
}

export default DtcsListing
